import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useInterval } from "react-use";
import { AutomedPaymentResult, bookingService } from "src/services/booking";
import { useDispatch, useSelector } from "src/store";
import { setPaymentStatus } from "src/store/bookingFlow";

type Props = {
    paymentMethod: number;
    patientName: string;
    onConfirm?(paymentId: number | string): void;
    onError?(message: string): void;
};

function isAutomedPaymentInitialisation(origin: string, data: unknown): data is AutomedPaymentInitialisation {
    return origin === import.meta.env.VITE_AUTOMED_URL
        && !!data && typeof data === 'object'
        && "title" in data
        && data.title === "Automed_payment_query";
}

function isAutomedPaymentEvent(origin: string, data: unknown): data is AutomedPaymentResult {
    return origin === import.meta.env.VITE_AUTOMED_URL
        && !!data && typeof data === 'object'
        && 'status' in data
        && 'msg' in data;
}

type AutomedPaymentInitialisation = {
    "title": "Automed_payment_query",
    "SessionId": string,
    "SST": string;
}

export function PaymentEmbed({ paymentMethod, onConfirm, patientName, onError }: Props) {
    const {
        appointment_id,
        doctor_id,
        location_id,
        user_fk,
        patient_id,
        clinic_group_crm,
        video_room_number,
    } = useSelector(state => state.appointment.reservedBooking!);
    const dispatch = useDispatch();
    const [paymentStatus] = useState(useSelector(state => state.bookingFlow.paymentStatus));

    const appointmentTypeId = useSelector(state => state.appointment.appointmentTypeId);
    const clinicId = useSelector(state => state.appointment.clinicId);
    useEffect(() => {
        dispatch(setPaymentStatus('STARTED'));
    }, [dispatch])

    const [sessionId, setSessionId] = useState<string>();
    const [sst, setSst] = useState<string>();

    const queryString = useMemo(() => {
        const q = new URLSearchParams({
            clinic_group_crm: clinic_group_crm.toString(),
            user_fk: user_fk.toString(),
            req: 'myhealth_mobile_api_payment',
            lid: location_id.toString(),
            did: doctor_id.toString(),
            pid: patient_id.toString(),
            pname: patientName,
            bid: appointment_id.toString(),
            pmethod: paymentMethod.toString(),
            aptypeid: appointmentTypeId.toString(),
        });
        if (video_room_number) {
            q.append("room_name", video_room_number);
        }
        return q;
    }, [appointmentTypeId, appointment_id, clinic_group_crm, doctor_id, location_id, patientName, patient_id, paymentMethod, user_fk, video_room_number]);

    const processPaymentResult = useCallback((data: AutomedPaymentResult) => {
        switch (Number(data.status)) {
            case 1: // Payment successful
                onConfirm?.(data.payment_id!);
                break;
            case 0: // Payment in progress
            case -4: // Payment process initiated
                break;
            case -1: // Payment unsuccessful
            case -2: // Session Expired
                toast.error(data.error || data.msg);
                onError?.(data.msg);
                break;
            // case -3: // Record not found
            // not handling this to prevent DEV env errors
        }
    }, [onConfirm, onError]);

    const messageCallback = useCallback(({ data, origin }: MessageEvent) => {
        console.debug(`postMessage from ${origin}:`, data)
        if (isAutomedPaymentInitialisation(origin, data)) {
            setSessionId(data.SessionId);
            setSst(data.SST);
        }
        if (isAutomedPaymentEvent(origin, data)) {
            processPaymentResult(data);
        }
    }, [processPaymentResult]);

    useInterval(useCallback(async () => {
        if (sessionId && sst) {
            const data = await bookingService.GetPaymentResult(clinicId, sessionId, sst);
            processPaymentResult(data);
        }
    }, [processPaymentResult, sessionId, sst, clinicId]), 1000 * 5);

    useEffect(() => {
        window.addEventListener("message", messageCallback);
        return () => {
            window.removeEventListener("message", messageCallback);
        }
    }, [messageCallback]);

    if (paymentStatus !== "NOT_STARTED") {
        // displaying iframe will create a new payment session (bad!);
        return null;
    }

    return <iframe
        className='w-full drop-shadow-xl border border-gray-200 rounded-xl'
        height={500}
        name="Myhealth_payment"
        src={`${import.meta.env.VITE_AUTOMED_URL}/myhealth/cors_api/myhealth_initiate_checkout?${queryString}`}
    />
}